import React from 'react';
import classNames from 'classnames';
import { useFormContext } from 'react-hook-form';
import { Testable, Tourable } from './types';
import { Tooltip } from './Tooltip';
import { LinkProps, Link } from 'react-router-dom';

const buttonTypes = {
  outline: 'bg-white hover:bg-gray-200 text-blue',
  monotone: 'bg-white hover:bg-gray-200 text-black',
  primary: 'bg-blue hover:bg-blue-700 text-white border-transparent',
  success: 'bg-green-500 text-white',
  danger: 'bg-red-500 hover:bg-red-600 text-white',
};

const buttonSizes = {
  normal: 'px-4 py-3 space-x-2',
  small: 'px-2 py-1 space-x-1',
};

interface Props extends LinkProps {
  variant?: keyof typeof buttonTypes;
  size?: keyof typeof buttonSizes;
  rightIcon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
  tooltip?: React.ReactNode;
  analyticsProperties?: { name: string; properties?: Record<string, any> };
  iconOnly?: boolean;
}

export type ButtonLinkProps = Tourable<Testable<Props>>;

export function ButtonLink({
  variant = 'primary',
  size = 'normal',
  fullWidth = true,
  rightIcon,
  leftIcon,
  children,
  className,
  loading,
  tooltip,
  analyticsProperties,
  iconOnly = false,
  ...buttonProps
}: ButtonLinkProps) {
  const formContext = useFormContext();
  const showLoading =
    loading ||
    (buttonProps.type === 'submit' && formContext?.formState.isSubmitting);

  const button = (
    <Link
      {...buttonProps}
      style={buttonProps.style}
      className={classNames(
        'flex  transition-all items-center border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
        buttonTypes[variant],
        leftIcon || rightIcon ? 'justify-between' : 'justify-center',
        buttonSizes[size],
        className,
        fullWidth && 'w-full',
      )}
    >
      {!!leftIcon && !showLoading && <div>{leftIcon}</div>}
      {!iconOnly && <div>{children}</div>}
      {!!rightIcon && !showLoading && <div>{rightIcon}</div>}
    </Link>
  );

  return tooltip ? <Tooltip tooltip={tooltip}>{button}</Tooltip> : button;
}
